//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';

// Proactively refresh the token if it expires within this 5-minute window to
// avoid serving requests with a token that will expire mid-flight.
const BUFFER_SECS = 5 * 60;

// TODO PD-311169 - remove extra handling once we migrate to Amplify v6
// Amplify v1 does not coalesce concurrent requests to refresh tokens
// This fn coalesces concurrent calls into a single inflight promise so only one refresh hits Cognito at a time
let inflightTokenPromise: Promise<string | undefined> | null = null;

function isTokenExpiringSoon(session: CognitoUserSession): boolean {
  const nowSecs = Date.now() / 1000;
  return session.getIdToken().getExpiration() - nowSecs < BUFFER_SECS;
}

export async function getJwtToken(): Promise<string | undefined> {
  if (inflightTokenPromise) {
    return inflightTokenPromise;
  }

  inflightTokenPromise = (async () => {
    let session: CognitoUserSession;
    try {
      session = await Auth.currentSession();
    } catch {
      return undefined;
    }

    if (!isTokenExpiringSoon(session)) {
      return session.getIdToken().getJwtToken();
    }

    try {
      const user = (await Auth.currentAuthenticatedUser()) as CognitoUser;
      const refreshed = await new Promise<CognitoUserSession>(
        (resolve, reject) => {
          user.refreshSession(
            session.getRefreshToken(),
            (err: unknown, result: CognitoUserSession) =>
              err
                ? reject(
                    err instanceof Error
                      ? err
                      : new Error('Token refresh failed'),
                  )
                : resolve(result),
          );
        },
      );
      return refreshed.getIdToken().getJwtToken();
    } catch {
      // Refresh failed (e.g. network blip) — fall back to the current token,
      // which may still be valid within the proactive buffer window.
      return session.getIdToken().getJwtToken();
    }
  })().finally(() => {
    inflightTokenPromise = null;
  });

  return inflightTokenPromise;
}

export async function getRequiredJwtToken(): Promise<string> {
  const jwt = await getJwtToken();

  if (jwt) {
    return jwt;
  }

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }

  throw new Error('No valid session');
}
